import { useCallback, useEffect, useRef, useState } from "react";
import { Conversation } from "@ac-expo/api-types";
import { Svg } from "../Svg";
import ConversationHeader from "./ConversationHeader";
import { cn } from "../../../utils";
import { ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";

export interface ConversationComponentProps {
  conversation: Conversation | undefined;
  sendMessage: (data: { toUserId: string; messageContent: string }) => void;
  pictureUrl?: string;
  withHeader?: boolean;
  fromBackoffice?: boolean;
  cannotSendMessage?: boolean;
}

export function ConversationComponent({
  conversation,
  sendMessage,
  pictureUrl,
  withHeader = true,
  fromBackoffice,
  cannotSendMessage,
}: ConversationComponentProps) {
  const messageEndRef = useRef<HTMLDivElement>(null);
  const conversationContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messageEndRef.current && conversationContainerRef.current) {
      conversationContainerRef.current.scrollTo({
        top: conversationContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      messageEndRef.current.scrollTo({ behavior: "smooth" });
    }
  }, [conversation?.with.id, conversation?.messages.length]);

  const [message, setMessage] = useState("");

  const handleSubmit = useCallback(async () => {
    if (message.trim()) {
      try {
        sendMessage({
          toUserId: conversation?.with.id ?? "",
          messageContent: message,
        });
        setMessage("");
        if (conversationContainerRef.current) {
          conversationContainerRef.current.scrollTo({
            top: conversationContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }
  }, [message, sendMessage, conversation?.with.id]);

  if (!conversation) {
    return (
      <div className={"flex w-full flex-col"}>
        <div
          className={
            "flex h-[84px] w-full items-center gap-4 border-b bg-white p-4"
          }
        />
        <div className="flex size-full flex-col items-center justify-center gap-4 bg-gray-50">
          <ChatCircle size={24} fill={"#287BB9"} />
          <p className="text-base text-gray-500">
            Aucune discussion pour le moment
          </p>
        </div>
      </div>
    );
  }

  const { messages, with: participant } = conversation;

  return (
    <div className="flex size-full flex-col ">
      {withHeader && (
        <ConversationHeader
          participant={participant}
          pictureUrl={pictureUrl}
          fromBackoffice={fromBackoffice}
        />
      )}
      <div
        className="flex h-full flex-col space-y-2 overflow-auto bg-gray-50 px-6 pt-6"
        ref={conversationContainerRef}
      >
        {messages
          .slice()
          .reverse()
          .map((message) => (
            <div
              key={message.id}
              className={cn("flex w-full", {
                "justify-start": message.senderId === participant.id,
                "justify-end pl-10": message.senderId !== participant.id,
              })}
            >
              <div
                className={cn(
                  "max-w-full text-wrap break-words rounded-b-lg px-4 py-2 text-sm",
                  {
                    "rounded-tr-lg border bg-white text-black":
                      message.senderId === participant.id,
                    "rounded-tl-lg bg-blue-700 text-white":
                      message.senderId !== participant.id,
                  }
                )}
              >
                {message.content}
              </div>
            </div>
          ))}
        <div ref={messageEndRef} />{" "}
      </div>
      {!cannotSendMessage && (
        <div className="flex items-center gap-3 border-t px-6 py-3">
          <input
            type="text"
            placeholder="Message"
            className="w-full rounded-lg border border-gray-200 p-4"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <button onClick={() => handleSubmit()}>
            <PaperPlaneTilt size={21} fill="#287BB9" weight="fill" />
          </button>
        </div>
      )}
    </div>
  );
}

export default ConversationComponent;
